/* eslint-disable max-len */
import { useFormikContext } from 'formik';
import { ChangeEvent, useEffect } from 'react';
import { getCurrentUserAccess } from '../../../services/base.service';
import { useAppSelector } from '../../../redux/hooks';
import { 
  selectNonPublicStudentFormViewModel,
  selectHasSearchMatch,
  selectHas200500SearchMatch,
  selectAllowEdit,
 } from '../../../redux/slices/nonPublicStudentSlice';
import { NonPublicStudentEntryModel } from '../../../types/NonPublicStudentEntryModel';
import {
  NonPublicStudentFormViewModel,
  NPSESchoolType,
} from '../../../types/NonPublicStudentFormViewModel';
import { PanelType } from '../../../types/PanelType';
import { FormRadioListOrientation } from '../../../types/propTypes/FormRadioListPropTypes';
import { NonPublicStudentEntryValidationSchema } from '../../../types/Validation/NonPublicStudentEntryValidationSchema';
import { isStateNonAdmin } from '../../../utilities/userUtilities';
import FormRadioList from '../../FormInputs/FormRadioList/FormRadioList';
import FormTextbox from '../../FormInputs/FormTextbox/FormTextbox';
import Panel from '../../Panel/Panel';
import './NPSE_LEAPanel.css';

const NPSE_LEAPanel : React.FC = () => {
  const { setFieldValue, values } = useFormikContext<NonPublicStudentEntryModel>();

  const formViewModel: NonPublicStudentFormViewModel = useAppSelector(
    selectNonPublicStudentFormViewModel
  );

  const hasSearchMatch: boolean = useAppSelector(
    selectHasSearchMatch
  );

  const has200500SearchMatch: boolean = useAppSelector(
    selectHas200500SearchMatch
  );

  const allowEdit: boolean = useAppSelector(
    selectAllowEdit
  )

  const areFormComponentsDisabled: boolean =
    !allowEdit || !hasSearchMatch || has200500SearchMatch || isStateNonAdmin();

  const prefillSchoolNameAndNumber = (schoolType: string): string => {
    /* istanbul ignore next */
    const districtId =
      getCurrentUserAccess()?.currentDistrict?.districtId || '';

    const schoolNameVal =
      schoolType === NPSESchoolType.CTE
        ? 'CTE Non-Public'
        : 'Special Ed Non-Public';
    const schoolNumberVal =
      schoolType === NPSESchoolType.CTE
        ? `${districtId}-200`
        : `${districtId}-500`;

    setFieldValue('schoolId', schoolNumberVal);
    setFieldValue('schoolName', schoolNameVal);
    setFieldValue('school', `${schoolNameVal} (${schoolNumberVal})`);

    return schoolNumberVal;
  };

  const clearNonUsedFieldForOtherSchoolType = (schoolType: string): void => {
    if (schoolType === NPSESchoolType.SPED) {
      setFieldValue('singleParentOrSinglePregnantWomanStatus', '');
      setFieldValue('cteaeDisplacedHomemakerIndicator', '');
      setFieldValue('perkinsPostProgramPlacementIndicator', '');
    } else {
      setFieldValue('eligibilityDeterminationDate', '');
      setFieldValue('iepStartDate', '');
      setFieldValue('projectedHSDiplomaType', '');
      setFieldValue('programParticipationExitDate', '');
      setFieldValue('primaryDisabilityType', '');
      setFieldValue('secondaryDisabilityType', '');
      setFieldValue('significantCognitiveDisabilityIndicator', '');
      setFieldValue('ideaEducationalEnvironment', '');
      setFieldValue('relatedServices', []);
      setFieldValue('outcomeAEntryScore', '');
      setFieldValue('outcomeAEntryScoreDate', '');
      setFieldValue('outcomeAExitScore', '');
      setFieldValue('outcomeAExitScoreDate', '');
      setFieldValue('outcomeAProgress', '');
      setFieldValue('outcomeBEntryScore', '');
      setFieldValue('outcomeBEntryScoreDate', '');
      setFieldValue('outcomeBExitScore', '');
      setFieldValue('outcomeBExitScoreDate', '');
      setFieldValue('outcomeBProgress', '');
      setFieldValue('outcomeCEntryScore', '');
      setFieldValue('outcomeCEntryScoreDate', '');
      setFieldValue('outcomeCExitScore', '');
      setFieldValue('outcomeCExitScoreDate', '');
      setFieldValue('outcomeCProgress', '');
    }
  };

  const handleSchoolTypeChange = (e: ChangeEvent<HTMLButtonElement>): void => {
    const schoolType = e.target.value;

    clearNonUsedFieldForOtherSchoolType(schoolType);
    prefillSchoolNameAndNumber(schoolType);
    setFieldValue('schoolTypeInitial', e.target.value);
  };

  // Set school field based on school type
  useEffect(() => {
    const currentUser = getCurrentUserAccess();
    const districtId = currentUser?.currentDistrict?.districtId;
    
    if (districtId && values.schoolType) {
      if (values.schoolType === NPSESchoolType.CTE) {
        setFieldValue('school', `CTE Non-Public (${districtId}-200)`);
      } else if (values.schoolType === NPSESchoolType.SPED) {
        setFieldValue('school', `Special Ed Non-Public (${districtId}-500)`);
      }
    }
  }, [values.schoolType, setFieldValue]);

  return (
    <Panel panelType={PanelType.INFO} heading="LEA & School Information">
      <div className="field-row field-row-lea">
        <FormTextbox
          displayName="LEA"
          field="district"
          disabled={true}
          validationSchema={NonPublicStudentEntryValidationSchema}
        />
        <FormTextbox
          displayName="School Year"
          field="schoolYear"
          disabled={true}
          validationSchema={NonPublicStudentEntryValidationSchema}
        />
        <FormRadioList
          displayName="School Type"
          field="schoolType"
          options={formViewModel.schoolTypeOptions}
          optionOrientation={FormRadioListOrientation.Horizontal}
          validationSchema={NonPublicStudentEntryValidationSchema}
          disabled={areFormComponentsDisabled}
          onClick={handleSchoolTypeChange}
        />
        <FormRadioList
          displayName="School Type Initial"
          field="schoolTypeInitial"
          options={formViewModel.schoolTypeOptions}
          optionOrientation={FormRadioListOrientation.Horizontal}
          validationSchema={NonPublicStudentEntryValidationSchema}
          disabled={true}
          classConfig={{ containerClasses: 'school-type-initial' }}
        />
        <FormTextbox
          displayName="School"
          field="school"
          disabled={true}
          validationSchema={NonPublicStudentEntryValidationSchema}
        />
      </div>
    </Panel>
  );
};

export default NPSE_LEAPanel;
